/**
 * GoodGuardian — Attorney Matcher Screen
 * Guided flow to find the right family law attorney.
 */
import { useState } from 'react'
import AppHeader from '@/components/layout/AppHeader'
import Button from '@/components/common/Button'
import { useApp } from '@/context/AppContext'
import { getToken } from '@/utils/auth'

const SITUATION_TYPES = [
  { value: 'custody',      label: '⚖️ Custody / Parenting Plan' },
  { value: 'divorce',      label: '📋 Divorce / Dissolution' },
  { value: 'cps',          label: '🛡️ CPS / Dependency' },
  { value: 'modification', label: '🔄 Modify Existing Order' },
  { value: 'support',      label: '💰 Child / Spousal Support' },
  { value: 'dv',           label: '🚨 Domestic Violence / Protection Order' },
  { value: 'general',      label: '📌 General Family Law' },
]

const URGENCY_OPTIONS = [
  { value: 'immediate', label: '🚨 Immediate',    desc: 'Court date within 2 weeks or child removed' },
  { value: 'urgent',    label: '⚡ Urgent',       desc: 'Need help within the next month' },
  { value: 'soon',      label: '📅 Soon',         desc: 'Within 1–3 months' },
  { value: 'planning',  label: '🔭 Planning Ahead', desc: 'No immediate deadline' },
]

const BUDGET_OPTIONS = [
  { value: 'no_budget', label: 'No Budget',       desc: 'Need free / pro bono help' },
  { value: 'limited',   label: 'Under $3,000',    desc: 'Limited scope or flat-fee services' },
  { value: 'moderate',  label: '$3,000–$10,000',  desc: 'Full representation with cost management' },
  { value: 'flexible',  label: '$10,000+',        desc: 'Full representation, flexible budget' },
]

function StepCard({ title, children }) {
  return (
    <div className="card p-5 mb-4">
      <h3 className="text-base font-semibold text-slate-700 mb-3">{title}</h3>
      {children}
    </div>
  )
}

function ResourceCard({ resources }) {
  if (!resources) return null
  return (
    <div className="card p-4 mb-4 border-l-4 border-blue-500">
      <p className="text-sm font-bold text-blue-700 uppercase tracking-wide mb-2">📍 Your State Resources</p>
      <div className="space-y-2 text-sm text-slate-700">
        <div>
          <p className="font-semibold text-slate-600">State Bar Referral</p>
          <p className="text-slate-500">{resources.bar}</p>
        </div>
        {resources.legalAid?.length > 0 && (
          <div>
            <p className="font-semibold text-slate-600">Free / Low-Cost Legal Aid</p>
            {resources.legalAid.map((r, i) => (
              <p key={i} className="text-slate-500">• {r}</p>
            ))}
          </div>
        )}
        <div>
          <p className="font-semibold text-slate-600">Self-Help Resources</p>
          <p className="text-slate-500">{resources.selfHelp}</p>
        </div>
        {resources.specialty && (
          <div>
            <p className="font-semibold text-slate-600">Specialty Services</p>
            <p className="text-slate-500">{resources.specialty}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function ResultView({ result, onReset }) {
  const [openSection, setOpenSection] = useState('how_to_find')

  const sections = [
    { key: 'how_to_find',      label: '🔍 How to Find an Attorney', list: true },
    { key: 'what_to_look_for', label: '✅ What to Look For',        list: true },
    { key: 'questions_to_ask', label: '❓ Questions to Ask',        list: true },
    { key: 'red_flags',        label: '🚩 Red Flags to Avoid',      list: true },
    { key: 'if_cant_afford',   label: '💙 If You Can\'t Afford It', list: true },
  ]

  return (
    <div className="space-y-4">
      {/* Header card */}
      <div className="card p-5 bg-gradient-to-br from-brand-700 to-indigo-700 text-white">
        <p className="text-sm font-semibold opacity-75 uppercase tracking-wide mb-1">You Need</p>
        <p className="text-base font-bold leading-snug mb-3">{result.attorney_type || 'A Family Law Attorney'}</p>
        <p className="text-base opacity-90 leading-relaxed">{result.overview}</p>
        {result.urgency_note && (
          <div className="mt-3 bg-white/20 rounded-xl px-3 py-2">
            <p className="text-sm font-semibold">⚡ {result.urgency_note}</p>
          </div>
        )}
      </div>

      {/* First step */}
      {result.first_step && (
        <div className="card p-4 border-l-4 border-green-500">
          <p className="text-sm font-bold text-green-700 uppercase tracking-wide mb-1">Do This Today</p>
          <p className="text-base text-slate-700 font-medium">{result.first_step}</p>
        </div>
      )}

      {/* Cost guidance */}
      {result.cost_guidance && (
        <div className="card p-4">
          <p className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-1">💰 Cost Guidance</p>
          <p className="text-base text-slate-700">{result.cost_guidance}</p>
        </div>
      )}

      {/* State resources */}
      <ResourceCard resources={result.state_resources} />

      {/* Expandable sections */}
      {sections.map(s => (
        result[s.key]?.length > 0 && (
          <div key={s.key} className="card overflow-hidden">
            <button
              onClick={() => setOpenSection(openSection === s.key ? null : s.key)}
              className="w-full flex items-center justify-between px-4 py-3 text-left"
            >
              <span className="text-sm font-semibold text-slate-800">{s.label}</span>
              <svg
                className={`w-4 h-4 text-slate-400 transition-transform ${openSection === s.key ? 'rotate-180' : ''}`}
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {openSection === s.key && (
              <div className="px-4 pb-4 space-y-2 border-t border-slate-100 pt-3">
                {result[s.key].map((item, i) => (
                  <div key={i} className="flex gap-2 text-base text-slate-700">
                    <span className="text-slate-400 flex-shrink-0 mt-0.5">{i + 1}.</span>
                    <span className="leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      ))}

      {/* Disclaimer */}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
        <p className="text-sm text-amber-700 leading-relaxed">
          ⚠️ This guidance is for educational purposes only. GoodGuardian does not endorse or recommend specific attorneys. Always verify an attorney's credentials through your state bar before retaining them.
        </p>
      </div>

      <Button onClick={onReset} className="w-full">Start Over</Button>
    </div>
  )
}

export default function AttorneyMatch() {
  const { user } = useApp()
  const [step, setStep]                 = useState(1)
  const [situationType, setSituationType] = useState('')
  const [urgency, setUrgency]           = useState('')
  const [budget, setBudget]             = useState('')
  const [details, setDetails]           = useState('')
  const [loading, setLoading]           = useState(false)
  const [result, setResult]             = useState(null)
  const [error, setError]               = useState('')

  const userState = user?.state || ''

  const handleSubmit = async () => {
    setLoading(true)
    setError('')
    try {
      const token = getToken()
      const res = await fetch('/api/attorney-match', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          state: userState,
          situation_type: situationType,
          urgency,
          budget,
          details,
        }),
      })
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      const data = await res.json()
      setResult(data)
    } catch (err) {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const reset = () => {
    setResult(null)
    setStep(1)
    setSituationType('')
    setUrgency('')
    setBudget('')
    setDetails('')
    setError('')
  }

  return (
    <div className="flex flex-col h-full bg-white">
      <AppHeader title="Find an Attorney" showBack />
      <div className="screen no-scrollbar px-4 py-5">

        {result ? (
          <ResultView result={result} onReset={reset} />
        ) : (
          <>
            {/* Intro */}
            <div className="card p-4 mb-5 bg-gradient-to-br from-brand-700 to-indigo-700 text-white">
              <p className="text-base font-bold mb-1">🏛️ Attorney Matcher</p>
              <p className="text-base opacity-90">
                Answer 3 quick questions and we'll give you personalized guidance on finding the right attorney for your situation
                {userState ? ` in ${userState}` : ''}.
              </p>
            </div>

            {/* Step 1: Situation */}
            <StepCard title="1. What's your primary legal need?">
              <div className="space-y-2">
                {SITUATION_TYPES.map(s => (
                  <button
                    key={s.value}
                    onClick={() => { setSituationType(s.value); setStep(Math.max(step, 2)) }}
                    className={`w-full text-left px-4 py-3 rounded-xl text-base border-2 transition-all ${
                      situationType === s.value
                        ? 'border-blue-500 bg-blue-50 text-blue-800 font-semibold'
                        : 'border-slate-200 bg-white text-slate-700'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </StepCard>

            {/* Step 2: Urgency */}
            {step >= 2 && (
              <StepCard title="2. How urgent is your situation?">
                <div className="space-y-2">
                  {URGENCY_OPTIONS.map(o => (
                    <button
                      key={o.value}
                      onClick={() => { setUrgency(o.value); setStep(Math.max(step, 3)) }}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                        urgency === o.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <p className={`text-sm font-semibold ${urgency === o.value ? 'text-blue-800' : 'text-slate-800'}`}>{o.label}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{o.desc}</p>
                    </button>
                  ))}
                </div>
              </StepCard>
            )}

            {/* Step 3: Budget */}
            {step >= 3 && (
              <StepCard title="3. What's your approximate budget for legal help?">
                <div className="space-y-2">
                  {BUDGET_OPTIONS.map(o => (
                    <button
                      key={o.value}
                      onClick={() => setBudget(o.value)}
                      className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                        budget === o.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white'
                      }`}
                    >
                      <p className={`text-sm font-semibold ${budget === o.value ? 'text-blue-800' : 'text-slate-800'}`}>{o.label}</p>
                      <p className="text-sm text-slate-500 mt-0.5">{o.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Optional details */}
                {budget && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-slate-600 mb-1">
                      Anything else we should know? (optional)
                    </label>
                    <textarea
                      className="w-full border border-slate-300 rounded-xl px-3 py-2.5 text-base text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                      placeholder="e.g. I have a hearing on April 15, I'm in Los Angeles, opposing party already has an attorney..."
                      value={details}
                      onChange={e => setDetails(e.target.value)}
                    />
                  </div>
                )}
              </StepCard>
            )}

            {/* Submit */}
            {situationType && urgency && budget && (
              <div className="space-y-3">
                {error && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>
                )}
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? 'Finding your match…' : 'Get My Attorney Guidance →'}
                </Button>
                <p className="text-center text-sm text-slate-600">
                  Powered by AI · Not legal advice · No attorney endorsement
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
